<template>
  <div class="user-breakdown-card">
    <h3 v-if="title">{{ title }}</h3>
    <div class="segments">
      <div v-for="segment in segments" :key="segment.label" class="segment">
        <p class="segment-value">{{ segment.value }}</p>
        <p class="segment-label">{{ segment.label }}</p>
        <p v-if="segment.detail" class="segment-detail">{{ segment.detail }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Segment {
  label: string;
  value: string | number;
  detail?: string;
}

interface Props {
  title?: string;
  segments: Segment[];
}

defineProps<Props>();
</script>

<style scoped>
.user-breakdown-card {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.2s ease;
  /* This card packs 3 segments where a plain StatsCard shows 1 number, but
     StatsGrid's auto-fit columns give every card the same single-column
     width — spanning 2 columns gives each segment enough room for its
     number and label. With fewer than 2 columns available (narrow/mobile
     grids), grid auto-placement just gives it the whole row instead of
     overflowing. */
  grid-column: span 2;
}

.user-breakdown-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.user-breakdown-card h3 {
  margin: 0 0 0.75rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
}

.segments {
  display: flex;
  align-items: stretch;
}

.segment {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 0 0.5rem;
}

.segment + .segment {
  border-left: 1px solid #e5e7eb;
}

.segment-value {
  margin: 0;
  font-size: 1.75rem;
  font-weight: bold;
  color: #3b82f6;
  line-height: 1;
  white-space: nowrap;
}

.segment-label {
  margin: 0.35rem 0 0 0;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.segment-detail {
  margin: 0.2rem 0 0 0;
  font-size: 0.7rem;
  color: #9ca3af;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .user-breakdown-card {
    padding: 0.75rem;
  }

  .segment-value {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .segments {
    flex-direction: column;
    gap: 0.6rem;
  }

  .segment + .segment {
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding-top: 0.6rem;
  }
}
</style>
